import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { PendingAgeLabel } from '@/components/orders/einvoice-pending-age-label'
import { orderHandoverAt } from '@/lib/einvoice-handover'
import { OrderLotDebtBadge } from '@/components/orders/order-lot-debt-badge'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { PaymentStatusBadge } from '@/components/sections/payment-status-badge'
import { SLABadge } from '@/components/sla-badge'
import { displayPhone, formatDatePaddedVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orders: SalesOrder[]
    isLoading: boolean
    emptyState: ReactNode
    /** Ghi tracking vị trí đơn được mở; thân thẻ mở đơn sau khi ghi. */
    onTrackOpen: (order: SalesOrder, index: number) => void
    onOpen: (order: SalesOrder) => void
    /** The pending-invoice list is showing: cards carry an age and open with its source. */
    pendingList?: boolean
}

/** Ô "đã chờ N ngày" trên thẻ của danh sách Chờ xuất hóa đơn; đơn chưa giao thì không có. */
function pendingAgeField(order: SalesOrder) {
    const handoverAt = orderHandoverAt(order)
    return handoverAt
        ? [
              {
                  label: m.einvoice_filter_label(),
                  value: <PendingAgeLabel deliveredAt={handoverAt} />,
              },
          ]
        : []
}

/** Danh sách đơn dạng thẻ cho màn hẹp (< md). */
export function OrdersListCards({
    orders,
    isLoading,
    emptyState,
    onTrackOpen,
    onOpen,
    pendingList,
}: Props) {
    if (isLoading)
        return (
            <div className="py-8 text-center text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    if (orders.length === 0) return <>{emptyState}</>
    return (
        <>
            {orders.map((order, index) => (
                <ListCard
                    key={order.id}
                    onClick={() => {
                        onTrackOpen(order, index)
                        onOpen(order)
                    }}
                    title={
                        <Link
                            to="/orders/$orderId"
                            params={{ orderId: order.id }}
                            search={
                                pendingList
                                    ? { from: 'pending_list' }
                                    : undefined
                            }
                            onClick={() => onTrackOpen(order, index)}
                            className={LIST_CARD_TITLE_CLASS}
                        >
                            {order.order_code}
                        </Link>
                    }
                    subtitle={
                        displayPhone(order.customer_phone)
                            ? `${order.customer_name} · ${order.customer_phone}`
                            : order.customer_name
                    }
                    badge={<OrderStatusBadge status={order.status} />}
                    metric={{
                        label: m.order_total(),
                        value: formatVnd(Math.round(order.total_amount)),
                    }}
                    fields={[
                        {
                            label: m.order_payment(),
                            value: (
                                <PaymentStatusBadge
                                    status={order.payment_status}
                                    short
                                />
                            ),
                        },
                        ...(order.lot_debt && order.lot_debt.items.length > 0
                            ? [
                                  {
                                      label: m.order_lot_debt_col(),
                                      value: (
                                          <OrderLotDebtBadge
                                              debt={order.lot_debt}
                                          />
                                      ),
                                  },
                              ]
                            : []),
                        ...(pendingList ? pendingAgeField(order) : []),
                        {
                            label: m.order_sla_label(),
                            value: (
                                <SLABadge
                                    deadlineAt={order.sla_deadline_at}
                                    breached={order.sla_breached}
                                    warningAt={order.sla_warning_at}
                                />
                            ),
                        },
                        {
                            label: m.order_created_date(),
                            value: formatDatePaddedVN(order.created_at),
                        },
                    ]}
                />
            ))}
        </>
    )
}
